import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { apiGet, apiPost, type AuthUser } from "../api.js";

// ─── Tipe respons backend (lihat apps/api/src/routes/admin.ts) ───────────────
type CompStatus = "ok" | "down" | "unknown";

interface UpstashUsage {
  available: boolean;
  used_commands?: number;
  cap_commands?: number;
  cap_usd?: number;
  percent?: number; // 0..1
  daily_rate?: number;
  eta_days?: number | null;
}
interface HealthResponse {
  api: CompStatus;
  database: CompStatus;
  redis: CompStatus;
  storage: CompStatus;
  worker: CompStatus;
  upstash: UpstashUsage;
}
interface QueueRow {
  queue: string;
  waiting: number;
  active: number;
  completed_24h: number;
  failed_24h: number;
}
interface TodayResponse {
  photos: number;
  fingers_done: number;
  klien_active: number;
  klien_approved: number;
  avg_seconds: number;
  stuck: number;
}
interface ActiveKlien {
  id: string;
  full_name: string;
  status: string;
  uploaded: number;
  processed: number;
  reviewed: number;
}

interface Props {
  user: AuthUser;
}

// ─── Helper tampilan ─────────────────────────────────────────────────────────
const QUEUE_LABEL: Record<string, string> = {
  stitch: "Gabung foto (stitch)",
  analyze: "Analisa pola",
  regenerate: "Gambar ulang skeleton",
  formula: "Hitung rumus",
  pdf: "Buat laporan PDF",
};

function dot(status: CompStatus): string {
  if (status === "ok") return "🟢";
  if (status === "down") return "🔴";
  return "🟡";
}
function statusWord(status: CompStatus): string {
  if (status === "ok") return "Normal";
  if (status === "down") return "Bermasalah";
  return "Tidak diketahui";
}

function fmtDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)} detik`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m} mnt ${s} dtk` : `${m} menit`;
}

function fmtNum(n: number | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("id-ID");
}

function fmtEta(days: number | null | undefined): string {
  if (days == null) return "tidak menuju batas";
  if (days < 1) return "kurang dari 1 hari";
  if (days > 365) return "lebih dari 1 tahun";
  return `± ${Math.round(days)} hari lagi`;
}

// ─── Kartu metrik kecil ──────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub }: { icon: string; label: string; value: string; sub?: string }) {
  return (
    <div className="card flex flex-col gap-1">
      <span className="text-xs text-slate-500">{icon} {label}</span>
      <span className="text-2xl font-semibold text-slate-900">{value}</span>
      {sub && <span className="text-xs text-slate-400">{sub}</span>}
    </div>
  );
}

// ─── Banner pemakaian Upstash (Redis) — multi-tingkat ────────────────────────
function UpstashBanner({ u }: { u: UpstashUsage | undefined }) {
  if (!u) return null;
  if (!u.available) {
    return (
      <div className="card bg-green-50 border-green-200 text-green-900 text-sm">
        🟢 Normal <span className="text-green-700">(data pemakaian Redis tidak tersedia saat ini)</span>
      </div>
    );
  }
  const pct = Math.round((u.percent ?? 0) * 100);
  const used = fmtNum(u.used_commands);
  const cap = fmtNum(u.cap_commands);
  const usd = u.cap_usd != null ? `$${u.cap_usd}/bulan` : "";
  const eta = fmtEta(u.eta_days);

  let cls = "bg-green-50 border-green-200 text-green-900";
  let emoji = "🟢";
  let headline = "Normal";
  if (pct >= 90) {
    cls = "bg-red-50 border-red-200 text-red-900";
    emoji = "🔴";
    headline = "KRITIS — sistem bisa berhenti";
  } else if (pct >= 70) {
    cls = "bg-amber-50 border-amber-200 text-amber-900";
    emoji = "🟡";
    headline = "Pemakaian mendekati batas, hubungi developer";
  }

  return (
    <div className={`card text-sm ${cls}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="font-medium">{emoji} {headline}</span>
        <span className="text-xs opacity-80">cap: {usd}</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white/60 overflow-hidden">
        <div
          className={pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-green-500"}
          style={{ width: `${Math.min(100, pct)}%`, height: "100%" }}
        />
      </div>
      <div className="mt-1.5 text-xs opacity-90">
        {used} / {cap} perintah ({pct}%) · estimasi sampai batas: {eta}
      </div>
    </div>
  );
}

export function PipelineMonitor({ user }: Props) {
  const allowed = user.role === "owner" || user.role === "super_admin";

  const health = useQuery<HealthResponse>({
    queryKey: ["pipe-health"],
    queryFn: () => apiGet<HealthResponse>("api/admin/pipeline/health"),
    refetchInterval: 5_000,
    enabled: allowed,
  });
  const queues = useQuery<{ queues: QueueRow[] }>({
    queryKey: ["pipe-queues"],
    queryFn: () => apiGet<{ queues: QueueRow[] }>("api/admin/pipeline/queues"),
    refetchInterval: 5_000,
    enabled: allowed,
  });
  const today = useQuery<TodayResponse>({
    queryKey: ["pipe-today"],
    queryFn: () => apiGet<TodayResponse>("api/admin/pipeline/today"),
    refetchInterval: 5_000,
    enabled: allowed,
  });
  const active = useQuery<{ active: ActiveKlien[] }>({
    queryKey: ["pipe-active"],
    queryFn: () => apiGet<{ active: ActiveKlien[] }>("api/admin/pipeline/active"),
    refetchInterval: 5_000,
    enabled: allowed,
  });

  const [retryMsg, setRetryMsg] = useState<string | null>(null);
  const retry = useMutation({
    mutationFn: () => apiPost<{ fingers_found: number; enqueued_count: number; failed_count: number }>(
      "api/admin/re-enqueue-stitch-pending",
    ),
    onSuccess: (r) =>
      setRetryMsg(
        r.fingers_found === 0
          ? "Tidak ada jari yang tersangkut — semua sudah jalan ✅"
          : `${r.enqueued_count} jari dijalankan ulang${r.failed_count > 0 ? `, ${r.failed_count} gagal (Redis masih bermasalah?)` : ""}.`,
      ),
    onError: (e) => setRetryMsg(`Gagal: ${e instanceof Error ? e.message : String(e)}`),
  });

  if (!allowed) {
    return <div className="card text-center text-slate-500">Halaman ini hanya untuk owner / super admin.</div>;
  }

  const h = health.data;
  const t = today.data;
  const stuck = t?.stuck ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Monitor Upload Langsung</h1>
          <p className="text-xs text-slate-500">Pantauan otomatis (refresh tiap 5 detik). Kalau semua 🟢 berarti sistem sehat.</p>
        </div>
        <span className="text-xs text-slate-400">
          {health.isFetching || queues.isFetching ? "Memperbarui…" : "Terbaru"}
        </span>
      </div>

      {/* Banner pemakaian Redis */}
      <UpstashBanner u={h?.upstash} />

      {/* Peringatan jari tersangkut */}
      {stuck > 0 && (
        <div className="card bg-amber-50 border-amber-200 text-amber-900 text-sm flex items-center justify-between flex-wrap gap-3">
          <span>⚠️ {stuck} jari sudah ter-upload lengkap tapi belum diproses lebih dari 5 menit.</span>
          <button
            type="button"
            onClick={() => retry.mutate()}
            disabled={retry.isPending}
            className="btn-primary text-xs"
          >
            {retry.isPending ? "Menjalankan…" : "Jalankan ulang yang tersangkut"}
          </button>
        </div>
      )}
      {retryMsg && <div className="card text-sm text-slate-700">{retryMsg}</div>}

      {/* 1. Status Kesehatan Sistem */}
      <section>
        <h2 className="text-sm font-semibold text-slate-600 mb-2">Status Kesehatan Sistem</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {h ? (
            [
              { k: "Website (API)", v: h.api },
              { k: "Database", v: h.database },
              { k: "Antrian (Redis)", v: h.redis },
              { k: "Penyimpanan foto", v: h.storage },
              { k: "Pekerja (Worker)", v: h.worker },
            ].map((c) => (
              <div key={c.k} className="card flex flex-col items-center text-center gap-1 py-3">
                <span className="text-2xl">{dot(c.v)}</span>
                <span className="text-xs font-medium text-slate-700">{c.k}</span>
                <span className="text-[11px] text-slate-400">{statusWord(c.v)}</span>
              </div>
            ))
          ) : (
            <div className="card col-span-full text-center text-slate-400 text-sm">Memuat status…</div>
          )}
        </div>
      </section>

      {/* 2. Aktivitas Hari Ini */}
      <section>
        <h2 className="text-sm font-semibold text-slate-600 mb-2">Aktivitas Hari Ini</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon="📷" label="Foto masuk" value={fmtNum(t?.photos)} />
          <StatCard icon="✋" label="Jari dianalisa" value={fmtNum(t?.fingers_done)} />
          <StatCard icon="👥" label="Klien aktif" value={fmtNum(t?.klien_active)} />
          <StatCard icon="✅" label="Laporan selesai" value={fmtNum(t?.klien_approved)} />
          <StatCard icon="⏱️" label="Rata-rata / jari" value={fmtDuration(t?.avg_seconds ?? 0)} />
          <StatCard icon="⚠️" label="Tersangkut" value={fmtNum(t?.stuck)} sub={stuck > 0 ? "perlu perhatian" : "aman"} />
        </div>
      </section>

      {/* 3. Statistik Antrian */}
      <section>
        <h2 className="text-sm font-semibold text-slate-600 mb-2">Antrian Pemrosesan</h2>
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Tahap</th>
                <th className="px-3 py-2 text-right font-medium">Menunggu</th>
                <th className="px-3 py-2 text-right font-medium">Diproses</th>
                <th className="px-3 py-2 text-right font-medium">Selesai (24j)</th>
                <th className="px-3 py-2 text-right font-medium">Gagal (24j)</th>
              </tr>
            </thead>
            <tbody>
              {queues.data?.queues.map((q) => (
                <tr key={q.queue} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-800">{QUEUE_LABEL[q.queue] ?? q.queue}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{fmtNum(q.waiting)}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{fmtNum(q.active)}</td>
                  <td className="px-3 py-2 text-right text-green-700">{fmtNum(q.completed_24h)}</td>
                  <td className={`px-3 py-2 text-right ${q.failed_24h > 0 ? "text-red-600 font-medium" : "text-slate-400"}`}>
                    {fmtNum(q.failed_24h)}
                  </td>
                </tr>
              ))}
              {!queues.data && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Memuat antrian…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 4. Klien Sedang Diproses */}
      <section>
        <h2 className="text-sm font-semibold text-slate-600 mb-2">Klien Sedang Diproses</h2>
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Nama Klien</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Ter-upload</th>
                <th className="px-3 py-2 text-right font-medium">Dianalisa</th>
                <th className="px-3 py-2 text-right font-medium">Disetujui</th>
              </tr>
            </thead>
            <tbody>
              {active.data?.active.map((k) => (
                <tr key={k.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{k.full_name}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <span className="px-2 py-0.5 rounded text-[11px] bg-slate-100">{k.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">{k.uploaded}/10</td>
                  <td className="px-3 py-2 text-right text-slate-700">{k.processed}/10</td>
                  <td className="px-3 py-2 text-right text-slate-700">{k.reviewed}/10</td>
                </tr>
              ))}
              {active.data && active.data.active.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Belum ada klien yang sedang diproses.</td></tr>
              )}
              {!active.data && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">Memuat…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
